import type { FieldPack } from '../../../../core/fields/types';
import { FieldType } from '../../../../core/fields/types';

export const orgchartWorkforceFieldPack: FieldPack = {
  id: 'orgchart-workforce',
  name: 'Workforce Planning',
  description: 'Tenure, retirement, contracts, and mobility data for workforce planning signals',
  version: '1.0.0',
  dependencies: ['orgchart-base'],
  fields: [
    {
      id: 'tenure',
      label: 'Tenure (Years)',
      type: FieldType.NUMBER,
      tags: ['workforce', 'timeline'],
      priority: 85,
      namespace: 'workforce',
      min: 0,
      step: 0.1,
      sensitivity: 'internal',
    },
    {
      id: 'retirementDate',
      label: 'Expected Retirement',
      type: FieldType.DATE,
      tags: ['workforce', 'timeline'],
      priority: 80,
      namespace: 'workforce',
      sensitivity: 'manager',
    },
    {
      id: 'contractEndDate',
      label: 'Contract End Date',
      type: FieldType.DATE,
      tags: ['workforce', 'timeline'],
      priority: 75,
      namespace: 'workforce',
      sensitivity: 'internal',
    },
    {
      id: 'probationEndDate',
      label: 'Probation End Date',
      type: FieldType.DATE,
      tags: ['workforce', 'timeline'],
      priority: 70,
      namespace: 'workforce',
      sensitivity: 'internal',
    },
    {
      id: 'lastPromotionDate',
      label: 'Last Promotion Date',
      type: FieldType.DATE,
      tags: ['workforce'],
      priority: 65,
      namespace: 'workforce',
      sensitivity: 'internal',
    },
    {
      id: 'promotionCount',
      label: 'Promotions',
      type: FieldType.NUMBER,
      tags: ['workforce'],
      priority: 60,
      namespace: 'workforce',
      min: 0,
      sensitivity: 'internal',
    },
    {
      id: 'timeInRole',
      label: 'Time in Current Role',
      type: FieldType.NUMBER,
      tags: ['workforce'],
      priority: 55,
      namespace: 'workforce',
      min: 0,
      step: 0.1,
      sensitivity: 'internal',
    },
    {
      id: 'previousRole',
      label: 'Previous Role',
      type: FieldType.SELECT,
      allowCustomOptions: true,
      tags: ['workforce'],
      priority: 50,
      namespace: 'workforce',
      sensitivity: 'internal',
    },
    {
      id: 'previousDepartment',
      label: 'Previous Department',
      type: FieldType.SELECT,
      allowCustomOptions: true,
      tags: ['workforce'],
      priority: 45,
      namespace: 'workforce',
      sensitivity: 'internal',
    },
    {
      id: 'mobilityPreference',
      label: 'Mobility Preference',
      type: FieldType.SELECT,
      tags: ['workforce'],
      priority: 40,
      namespace: 'workforce',
      options: [
        { value: 'open', label: 'Open to Relocation' },
        { value: 'limited', label: 'Limited Mobility' },
        { value: 'none', label: 'No Relocation' },
      ],
      sensitivity: 'manager',
    },
    {
      id: 'careerInterest',
      label: 'Career Interest',
      type: FieldType.TEXT,
      tags: ['workforce'],
      priority: 35,
      namespace: 'workforce',
      sensitivity: 'manager',
    },
    {
      id: 'targetRole',
      label: 'Target Role',
      type: FieldType.SELECT,
      allowCustomOptions: true,
      tags: ['workforce'],
      priority: 30,
      namespace: 'workforce',
      sensitivity: 'manager',
    },
    {
      id: 'noticePeriod',
      label: 'Notice Period',
      type: FieldType.TEXT,
      tags: ['workforce', 'compliance'],
      priority: 25,
      namespace: 'workforce',
      sensitivity: 'internal',
    },
    {
      id: 'visaStatus',
      label: 'Visa Status',
      // Work-authorization *categories* are a classification, not the
      // employee's data — the sensitive part is which person holds which, and
      // that stays guarded per value. Options are deliberately jurisdiction
      // neutral: this product serves AU/EU/APAC orgs, so shipping US visa
      // classes (H-1B, OPT…) would be wrong for most of them. Custom entry
      // covers the specific class where a team tracks it.
      type: FieldType.SELECT,
      allowCustomOptions: true,
      tags: ['workforce', 'compliance'],
      priority: 20,
      namespace: 'workforce',
      options: [
        { value: 'citizen', label: 'Citizen' },
        { value: 'permanent_resident', label: 'Permanent resident' },
        { value: 'work_visa', label: 'Work visa / permit' },
        { value: 'dependent_work_rights', label: 'Dependent visa (work rights)' },
        { value: 'student_visa', label: 'Student visa' },
        { value: 'requires_sponsorship', label: 'Requires sponsorship' },
        { value: 'not_applicable', label: 'Not applicable' },
      ],
      sensitivity: 'hr_confidential',
    },
    {
      id: 'visaExpiry',
      label: 'Visa Expiry',
      type: FieldType.DATE,
      tags: ['workforce', 'compliance'],
      priority: 15,
      namespace: 'workforce',
      sensitivity: 'hr_confidential',
    },
  ],
};
